/**
 * American BioLabs — ad-click capture-to-order bridge (file name kept for
 * theme compatibility; it started as the Google Ads bridge).
 *
 * Captures Google click identifiers and campaign parameters, plus the click
 * identifiers of Meta (fbclid), TikTok (ttclid), Microsoft (msclkid), Taboola
 * (tblci) and AppLovin (aleid), that arrive on the Shopify storefront. It
 * persists them only when Shopify allows marketing processing, and mirrors
 * them to private Shopify cart attributes. Those cart attributes carry into
 * the order for later server-side conversion matching.
 *
 * v2 storage model:
 * - The Google record keeps the exact v1 format and the v1 storage key, so a
 *   rollback to v1 still reads it. It feeds the 12 original attributes.
 * - Every other platform has its own slot with its own timestamp, stored under
 *   a separate v2 key together with the last-touch pointer.
 * - Records expire 90 days after their own click time.
 *
 * This module is deliberately independent of product forms, Recharge, cart
 * rendering, and checkout routing. It never replaces or intercepts cart adds.
 */
(function () {
  'use strict';

  if (window.ABLGoogleAttribution) return;

  var VERSION = '2.0.0';
  var STORAGE_KEY = 'abl_google_attribution_v1';
  var STORAGE_KEY_V2 = 'abl_attribution_v2';
  var SESSION_SYNC_KEY = 'abl_google_attribution_synced_v1';
  var COOKIE_NAME = 'abl_google_attribution';
  var COOKIE_NAME_V2 = 'abl_attribution_v2';
  var RECORD_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  /* Cookies are only a fallback for a failed localStorage write. Large ones
     are skipped so the store's request headers stay small. */
  var COOKIE_MAX_LENGTH = 1800;
  var MAX_VALUE_LENGTH = 512;
  var MAX_CLICK_ID_LENGTH = 1024;
  var PRIVACY_MAX_ATTEMPTS = 20;
  /* A deny event is applied only after this window, so a banner that
     signals a default deny and grants a moment later never wipes records. */
  var DENY_GRACE_MS = 1500;

  var PARAMETER_KEYS = [
    'gclid',
    'wbraid',
    'gbraid',
    'gad_source',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  var GOOGLE_CLICK_KEYS = ['gclid', 'wbraid', 'gbraid'];

  var UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  var ATTRIBUTE_NAMES = {
    gclid: '__gclid',
    wbraid: '__wbraid',
    gbraid: '__gbraid',
    gad_source: '__gad_source',
    utm_source: '__utm_source',
    utm_medium: '__utm_medium',
    utm_campaign: '__utm_campaign',
    utm_term: '__utm_term',
    utm_content: '__utm_content'
  };

  /* Order also sets last-touch priority when one URL carries several IDs. */
  var PLATFORM_SLOTS = [
    { key: 'fbclid', platform: 'meta' },
    { key: 'ttclid', platform: 'tiktok' },
    { key: 'msclkid', platform: 'microsoft' },
    { key: 'tblci', platform: 'taboola' },
    { key: 'aleid', platform: 'applovin' }
  ];

  var TOUCH_NAMES = ['google', 'meta', 'tiktok', 'microsoft', 'taboola', 'applovin', 'utm'];

  var arrivalTime = new Date();
  var arrivalIso = arrivalTime.toISOString();
  var incoming = readIncomingParameters();
  var attribution = null;
  var marketingAllowed = false;
  var everAllowed = false;
  var consentResolved = false;
  var denyPending = false;
  var denyToken = 0;
  var privacyLoadRequested = false;
  var privacyAttempts = 0;
  var pendingSync = null;
  var queuedSync = null;
  var retriesRemaining = 1;

  function sanitize(value) {
    if (typeof value !== 'string') return '';
    var cleaned = value.replace(/[\u0000-\u001F\u007F]/g, '').trim();
    if (!cleaned || /^(?:undefined|null)$/i.test(cleaned) || /^\{[^}]+\}$/.test(cleaned)) return '';
    return cleaned;
  }

  function cleanValue(value) {
    return sanitize(value).slice(0, MAX_VALUE_LENGTH);
  }

  /* A truncated click ID matches nothing on the ad platform, so an over-long
     value is skipped rather than cut. */
  function cleanClickId(value) {
    var cleaned = sanitize(value);
    return cleaned.length > MAX_CLICK_ID_LENGTH ? '' : cleaned;
  }

  function isGoogleClickKey(key) {
    return GOOGLE_CLICK_KEYS.indexOf(key) !== -1;
  }

  function cleanParameter(key, value) {
    return isGoogleClickKey(key) ? cleanClickId(value) : cleanValue(value);
  }

  /* Milliseconds for an ISO timestamp, or NaN when it cannot be read. */
  function timeOf(value) {
    return typeof value === 'string' ? Date.parse(value) : NaN;
  }

  function nowMs() {
    return new Date().getTime();
  }

  function isExpired(value) {
    var time = timeOf(value);
    return !isNaN(time) && time < nowMs() - RECORD_MAX_AGE_MS;
  }

  function readIncomingParameters() {
    var values = { parameters: {}, platformIds: {} };
    var search;

    try {
      search = new URLSearchParams(window.location.search || '');
    } catch (error) {
      return values;
    }

    PARAMETER_KEYS.forEach(function (key) {
      var value = cleanParameter(key, search.get(key));
      if (value) values.parameters[key] = value;
    });

    /* CheckoutChamp historically forwarded this alias. Treat it as a GCLID
       only when the canonical gclid parameter is absent. */
    if (!values.parameters.gclid) {
      var forwardedGclid = cleanClickId(search.get('cc_custom_gclid'));
      if (forwardedGclid) values.parameters.gclid = forwardedGclid;
    }

    PLATFORM_SLOTS.forEach(function (slot) {
      var value = cleanClickId(search.get(slot.key));
      if (value) values.platformIds[slot.key] = value;
    });

    return values;
  }

  function hasClickIdentifier(values) {
    return Boolean(values && (values.gclid || values.wbraid || values.gbraid));
  }

  function hasCampaignParameter(values) {
    if (!values) return false;
    return PARAMETER_KEYS.some(function (key) { return Boolean(values[key]); });
  }

  function hasUtmParameter(values) {
    if (!values) return false;
    return UTM_KEYS.some(function (key) { return Boolean(values[key]); });
  }

  function sameValues(keys, left, right) {
    return keys.every(function (key) {
      return (left[key] || '') === (right[key] || '');
    });
  }

  function sameParameters(left, right) {
    return sameValues(PARAMETER_KEYS, left, right);
  }

  /* One Google click is identified by its click IDs alone. */
  function sameGoogleClick(left, right) {
    return sameValues(GOOGLE_CLICK_KEYS, left, right);
  }

  function normalizeRecord(candidate) {
    if (!candidate || typeof candidate !== 'object' || !candidate.parameters) return null;

    var parameters = {};
    PARAMETER_KEYS.forEach(function (key) {
      var value = cleanParameter(key, candidate.parameters[key]);
      if (value) parameters[key] = value;
    });

    if (!hasCampaignParameter(parameters)) return null;

    return {
      version: 1,
      captured_at: cleanValue(candidate.captured_at) || new Date().toISOString(),
      parameters: parameters
    };
  }

  function createRecord(parameters, capturedAt) {
    return normalizeRecord({
      version: 1,
      captured_at: capturedAt || arrivalIso,
      parameters: parameters
    });
  }

  function googleTouchName(record) {
    return record && hasClickIdentifier(record.parameters) ? 'google' : 'utm';
  }

  function emptyState() {
    return { google: null, slots: {}, last_touch: '', captured_at: '' };
  }

  function hasAttribution(state) {
    if (!state) return false;
    if (state.google) return true;
    return PLATFORM_SLOTS.some(function (slot) { return Boolean(state.slots[slot.key]); });
  }

  function copyState(state) {
    var copy = emptyState();
    copy.google = state.google;
    copy.last_touch = state.last_touch;
    copy.captured_at = state.captured_at;
    PLATFORM_SLOTS.forEach(function (slot) {
      if (state.slots[slot.key]) copy.slots[slot.key] = state.slots[slot.key];
    });
    return copy;
  }

  /* Drops every part older than 90 days, each measured from its own click. */
  function expireState(state) {
    if (!state) return null;
    var fresh = copyState(state);
    if (fresh.google && isExpired(fresh.google.captured_at)) fresh.google = null;
    PLATFORM_SLOTS.forEach(function (slot) {
      var entry = fresh.slots[slot.key];
      if (entry && isExpired(entry.at)) delete fresh.slots[slot.key];
    });
    if (isExpired(fresh.captured_at)) {
      fresh.last_touch = '';
      fresh.captured_at = '';
    }
    return hasAttribution(fresh) ? fresh : null;
  }

  function newerOf(left, right, field) {
    if (!left) return right || null;
    if (!right) return left;
    return timeOf(left[field]) > timeOf(right[field]) ? left : right;
  }

  /* Another tab, or this page before a bfcache restore, may have stored a
     newer touch. Each part keeps its newest version; ties go to storage. */
  function mergeStates(memory, stored) {
    if (!memory) return stored;
    if (!stored) return memory;
    var merged = emptyState();
    merged.google = newerOf(memory.google, stored.google, 'captured_at');
    PLATFORM_SLOTS.forEach(function (slot) {
      var entry = newerOf(memory.slots[slot.key], stored.slots[slot.key], 'at');
      if (entry) merged.slots[slot.key] = entry;
    });
    var pointer = newerOf(
      memory.last_touch ? memory : null,
      stored.last_touch ? stored : null,
      'captured_at'
    );
    if (pointer) {
      merged.last_touch = pointer.last_touch;
      merged.captured_at = pointer.captured_at;
    }
    return merged;
  }

  function readCookie(name) {
    var prefix = name + '=';
    var cookies = document.cookie ? document.cookie.split(';') : [];

    for (var i = 0; i < cookies.length; i += 1) {
      var part = cookies[i].trim();
      if (part.indexOf(prefix) !== 0) continue;
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch (error) {
        return '';
      }
    }

    return '';
  }

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function parseJson(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  /* localStorage first; the cookie also covers a missing or unreadable value. */
  function loadStoredValue(storageKey, cookieName, normalize) {
    return normalize(parseJson(readStorage(storageKey))) || normalize(parseJson(readCookie(cookieName)));
  }

  function loadStoredRecord() {
    return loadStoredValue(STORAGE_KEY, COOKIE_NAME, normalizeRecord);
  }

  function normalizeSlots(candidate) {
    var slots = {};
    if (!candidate || typeof candidate !== 'object') return slots;

    PLATFORM_SLOTS.forEach(function (slot) {
      var entry = candidate[slot.key];
      if (!entry || typeof entry !== 'object') return;
      var value = cleanClickId(entry.value);
      var at = cleanValue(entry.at);
      if (value && at) slots[slot.key] = { value: value, at: at };
    });

    return slots;
  }

  function normalizePlatformState(candidate) {
    if (!candidate || typeof candidate !== 'object' || candidate.version !== 2) return null;
    var lastTouch = cleanValue(candidate.last_touch);
    return {
      slots: normalizeSlots(candidate.slots),
      last_touch: TOUCH_NAMES.indexOf(lastTouch) !== -1 ? lastTouch : '',
      captured_at: cleanValue(candidate.captured_at),
      google_at: cleanValue(candidate.google_at)
    };
  }

  function loadStoredPlatformState() {
    return loadStoredValue(STORAGE_KEY_V2, COOKIE_NAME_V2, normalizePlatformState);
  }

  function loadState() {
    var google = loadStoredRecord();
    var platform = loadStoredPlatformState();
    var state = emptyState();

    state.google = google;
    if (platform) {
      state.slots = platform.slots;
      state.last_touch = platform.last_touch;
      state.captured_at = platform.captured_at;
    }

    /* A Google record that v2 did not write itself (v1-only visitor, or a
       record created while the theme was rolled back) is the latest touch.
       Comparing with the capture time v2 last saw avoids trusting a skewed
       device clock. */
    if (google && (!platform || google.captured_at !== platform.google_at)) {
      state.last_touch = googleTouchName(google);
      state.captured_at = google.captured_at;
    }

    return expireState(state);
  }

  /* The state to post or inject: storage is re-read so the newest touch from
     another tab is used, never blanked. */
  function currentAttribution() {
    return expireState(mergeStates(attribution, loadState()));
  }

  function writeCookie(name, value, maxAgeSeconds) {
    var cookie = name + '=' + value +
      '; Max-Age=' + maxAgeSeconds + '; Path=/; SameSite=Lax';
    if (window.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  function expireCookie(name) {
    document.cookie = name + '=; Max-Age=0; Path=/; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  }

  /* Fallback cookie: capped in size, and its Max-Age ends when the record
     reaches 90 days, so page views never extend it. */
  function writeFallbackCookie(name, serialized, newestMs) {
    var encoded = encodeURIComponent(serialized);
    var maxAge = isNaN(newestMs)
      ? Math.floor(RECORD_MAX_AGE_MS / 1000)
      : Math.floor((newestMs + RECORD_MAX_AGE_MS - nowMs()) / 1000);

    if (encoded.length > COOKIE_MAX_LENGTH || maxAge <= 0) {
      if (readCookie(name)) expireCookie(name);
      return;
    }
    writeCookie(name, encoded, maxAge);
  }

  function persistValue(storageKey, cookieName, serialized, newestMs) {
    if (writeStorage(storageKey, serialized)) {
      /* v1 and v2 both read localStorage first; a leftover cookie is stale. */
      if (readCookie(cookieName)) expireCookie(cookieName);
      return;
    }
    writeFallbackCookie(cookieName, serialized, newestMs);
  }

  function removeStoredValue(storageKey, cookieName) {
    if (readStorage(storageKey)) {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        /* The cookie below is still removed. */
      }
    }
    if (readCookie(cookieName)) expireCookie(cookieName);
  }

  function persistState(state) {
    /* The v1 key keeps receiving the Google record so a rollback keeps it. */
    if (state.google) {
      persistValue(STORAGE_KEY, COOKIE_NAME, JSON.stringify(state.google), timeOf(state.google.captured_at));
    } else {
      removeStoredValue(STORAGE_KEY, COOKIE_NAME);
    }

    var newest = NaN;
    PLATFORM_SLOTS.forEach(function (slot) {
      var entry = state.slots[slot.key];
      var time = entry ? timeOf(entry.at) : NaN;
      if (!isNaN(time) && (isNaN(newest) || time > newest)) newest = time;
    });

    persistValue(STORAGE_KEY_V2, COOKIE_NAME_V2, JSON.stringify({
      version: 2,
      slots: state.slots,
      last_touch: state.last_touch,
      captured_at: state.captured_at,
      google_at: state.google ? state.google.captured_at : ''
    }), newest);
  }

  function hasStoredRecords() {
    return Boolean(
      readStorage(STORAGE_KEY) || readStorage(STORAGE_KEY_V2) ||
      readCookie(COOKIE_NAME) || readCookie(COOKIE_NAME_V2)
    );
  }

  function clearPersistence() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(STORAGE_KEY_V2);
      window.sessionStorage.removeItem(SESSION_SYNC_KEY);
    } catch (error) {
      /* Cookie removal below still runs when browser storage is unavailable. */
    }

    expireCookie(COOKIE_NAME);
    expireCookie(COOKIE_NAME_V2);
  }

  function hostKey(hostname) {
    return String(hostname || '').toLowerCase().replace(/^www\./, '');
  }

  /* Internal store links (advertorials, quiz handoff) carry hardcoded UTMs.
     Only an arrival from outside the store counts as a new UTM touch. */
  function isExternalArrival() {
    var referrer = document.referrer || '';
    if (!referrer) return true;
    var match = /^[a-z][a-z0-9+.\-]*:\/\/(?:[^\/?#@]*@)?([^\/?#:]+)/i.exec(referrer);
    if (!match) return true;
    return hostKey(match[1]) !== hostKey(window.location.hostname);
  }

  /* A reload or a history/bfcache navigation re-runs an old URL; it is not a
     new click. Browsers without the Navigation Timing API are treated as a
     normal navigation. */
  function isRepeatNavigation() {
    try {
      var performance = window.performance;
      if (!performance) return false;
      if (typeof performance.getEntriesByType === 'function') {
        var entries = performance.getEntriesByType('navigation');
        if (entries && entries[0] && entries[0].type) {
          return entries[0].type === 'reload' || entries[0].type === 'back_forward';
        }
      }
      if (performance.navigation) {
        return performance.navigation.type === 1 || performance.navigation.type === 2;
      }
    } catch (error) {
      /* Fall through to a normal navigation. */
    }
    return false;
  }

  function replaceUtms(record, parameters) {
    /* Without a stored Google click there is nothing to keep: start over. */
    if (!hasClickIdentifier(record.parameters)) {
      return sameParameters(record.parameters, parameters) ? null : createRecord(parameters);
    }

    var merged = {};
    PARAMETER_KEYS.forEach(function (key) {
      var source = UTM_KEYS.indexOf(key) !== -1 ? parameters : record.parameters;
      if (source[key]) merged[key] = source[key];
    });

    if (sameParameters(record.parameters, merged)) return null;

    /* The Google click keeps its own capture time. */
    return createRecord(merged, record.captured_at);
  }

  function selectAttribution() {
    var stored = loadState();
    if (stored && isRepeatNavigation()) return stored;

    var state = stored ? copyState(stored) : emptyState();
    var parameters = incoming.parameters;
    var touches = [];
    var googleTouch = false;

    if (hasClickIdentifier(parameters)) {
      googleTouch = true;
      if (state.google && sameGoogleClick(state.google.parameters, parameters)) {
        /* The same Google click again (history visit, restored tab, quiz
           handoff): the record follows the URL as in v1, but the click keeps
           its capture time and is not a new touch. */
        if (!sameParameters(state.google.parameters, parameters)) {
          state.google = createRecord(parameters, state.google.captured_at);
        }
      } else {
        /* A new Google click replaces the Google record (IDs and UTMs)
           exactly as in v1. */
        state.google = createRecord(parameters);
        touches.push('google');
      }
    }

    /* Every other platform click ID is a new touch that replaces only its own
       slot. A repeat of the stored value is not a new touch. */
    PLATFORM_SLOTS.forEach(function (slot) {
      var value = incoming.platformIds[slot.key];
      if (!value) return;
      var current = state.slots[slot.key];
      if (current && current.value === value) return;
      state.slots[slot.key] = { value: value, at: arrivalIso };
      touches.push(slot.platform);
    });

    if (!googleTouch) {
      if (!state.google) {
        /* As in v1, the first campaign parameters create the Google record. */
        if (hasCampaignParameter(parameters)) {
          state.google = createRecord(parameters);
          if (isExternalArrival() || touches.length || !state.last_touch) touches.push('utm');
        }
      } else if (hasUtmParameter(parameters) && (touches.length || isExternalArrival())) {
        /* An external UTM arrival (or the UTMs of a new platform click)
           replaces the UTMs and keeps every click ID. A same-origin UTM link
           never overwrites. */
        var replaced = replaceUtms(state.google, parameters);
        if (replaced) {
          state.google = replaced;
          touches.push('utm');
        }
      }
    }

    if (touches.length) {
      state.last_touch = touches[0];
      state.captured_at = arrivalIso;
    }

    return hasAttribution(state) ? state : null;
  }

  function clickIdentifierType(parameters) {
    if (parameters.gclid) return 'gclid';
    if (parameters.wbraid) return 'wbraid';
    if (parameters.gbraid) return 'gbraid';
    return '';
  }

  function readFbcCookie() {
    var value = sanitize(readCookie('_fbc'));
    var match = /^fb\.\d+\.(\d+)\.(.+)$/.exec(value);
    if (!match || value.length > MAX_CLICK_ID_LENGTH) return null;
    return { value: value, ms: Number(match[1]), payload: match[2] };
  }

  /* Meta's own _fbc cookie wins when it holds this fbclid (optionally with
     Meta's ".<appendix>") or a newer click; otherwise the value is built from
     the arrival time. */
  function resolveFbc(slot) {
    var arrivalMs = timeOf(slot.at);
    if (isNaN(arrivalMs)) return '';
    var cookie = readFbcCookie();
    if (cookie && (
      cookie.payload === slot.value ||
      cookie.payload.indexOf(slot.value + '.') === 0 ||
      cookie.ms >= arrivalMs
    )) return cookie.value;
    return 'fb.1.' + arrivalMs + '.' + slot.value;
  }

  function buildPrivateAttributes(state, clear) {
    var attributes = {};
    var record = clear || !state ? null : state.google;
    var parameters = record ? record.parameters : {};

    PARAMETER_KEYS.forEach(function (key) {
      /* Sending empty values removes stale identifiers from an older cart when
         a newer Google click uses a different identifier type. */
      attributes[ATTRIBUTE_NAMES[key]] = parameters[key] || '';
    });

    attributes.__google_click_id_type = record ? clickIdentifierType(parameters) : '';
    attributes.__google_click_captured_at = record ? record.captured_at : '';
    attributes.__google_attribution_version = record ? '1' : '';

    PLATFORM_SLOTS.forEach(function (slot) {
      var entry = clear || !state ? null : state.slots[slot.key];
      attributes['__' + slot.key] = entry ? entry.value : '';
      attributes['__' + slot.key + '_at'] = entry ? entry.at : '';
      if (slot.key === 'fbclid') attributes.__fbc = entry ? resolveFbc(entry) : '';
    });

    attributes.__attr_last_touch = clear || !state ? '' : state.last_touch;
    attributes.__attr_captured_at = clear || !state ? '' : state.captured_at;
    attributes.__attr_version = clear || !state ? '' : '2';

    return attributes;
  }

  function cartRoute() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'cart/update.js';
  }

  function postAttributes(attributes) {
    return window.fetch(cartRoute(), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('Attribution cart update failed');
      return true;
    });
  }

  function readSessionFingerprint() {
    try {
      return window.sessionStorage.getItem(SESSION_SYNC_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  function writeSessionFingerprint(value) {
    try {
      window.sessionStorage.setItem(SESSION_SYNC_KEY, value);
    } catch (error) {
      /* A blocked sessionStorage only means a harmless extra cart update. */
    }
  }

  /* Marketing must be allowed on this page, and the live API (when present)
     must still agree: consent revoked in another tab stops writes here too. */
  function writesAllowed() {
    if (!consentResolved || !marketingAllowed) return false;
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.marketingAllowed === 'function') {
      try {
        return Boolean(privacy.marketingAllowed());
      } catch (error) {
        return true;
      }
    }
    return true;
  }

  function syncCart(force) {
    if (!writesAllowed()) return Promise.resolve(false);
    if (pendingSync) {
      /* A forced sync (for example cart:added) during an in-flight one runs
         once more after it, because the add may have created a new cart. */
      if (!force) return pendingSync;
      if (!queuedSync) {
        var after = function () {
          queuedSync = null;
          return syncCart(true);
        };
        queuedSync = pendingSync.then(after, after);
      }
      return queuedSync;
    }

    attribution = currentAttribution();
    if (!attribution) return Promise.resolve(false);

    var attributes = buildPrivateAttributes(attribution, false);
    var fingerprint = JSON.stringify(attributes);
    if (!force && readSessionFingerprint() === fingerprint) return Promise.resolve(true);

    var request = postAttributes(attributes).then(function () {
      writeSessionFingerprint(fingerprint);
      retriesRemaining = 1;
      document.dispatchEvent(new CustomEvent('abl:google-attribution-synced'));
      return true;
    }, function () {
      if (retriesRemaining > 0) {
        retriesRemaining -= 1;
        window.setTimeout(function () { syncCart(true); }, 1200);
      }
      return false;
    });

    pendingSync = request;
    request.then(function () {
      if (pendingSync === request) pendingSync = null;
    });
    return request;
  }

  function clearCartAttribution() {
    var clear = function () {
      return postAttributes(buildPrivateAttributes(null, true)).then(function () {
        return true;
      }, function () {
        return false;
      });
    };

    return pendingSync ? pendingSync.then(clear, clear) : clear();
  }

  function injectCheckoutAttributes(event) {
    if (!writesAllowed()) return;
    if (!(event.target instanceof HTMLFormElement)) return;

    /* Older Safari versions can omit SubmitEvent.submitter. The focused
       checkout control is a safe fallback and does not alter form behavior. */
    var submitter = event.submitter;
    if (!submitter && document.activeElement && document.activeElement.form === event.target) {
      submitter = document.activeElement;
    }
    if (!submitter || submitter.name !== 'checkout') return;

    attribution = currentAttribution();
    if (!attribution) return;

    var form = event.target;
    var attributes = buildPrivateAttributes(attribution, false);

    Object.keys(attributes).forEach(function (attributeName) {
      var fieldName = 'attributes[' + attributeName + ']';
      var input = form.elements.namedItem(fieldName);

      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = fieldName;
        form.appendChild(input);
      }

      input.value = attributes[attributeName];
    });
  }

  function grantConsent() {
    denyPending = false;
    denyToken += 1;
    if (consentResolved && marketingAllowed) return;

    consentResolved = true;
    marketingAllowed = true;
    everAllowed = true;

    attribution = selectAttribution();
    if (!attribution) return;
    persistState(attribution);
    syncCart(false);
  }

  function finalizeDeny() {
    denyPending = false;
    var hadStoredRecords = hasStoredRecords();
    attribution = null;
    clearPersistence();
    /* Blank the cart when this page had shared attributes, or when an
       earlier visit stored records that may already be on the cart. */
    if (everAllowed || hadStoredRecords) clearCartAttribution();
  }

  /* An explicit deny stops every write at once and clears stored records and
     cart attributes after DENY_GRACE_MS, unless a grant arrives first. */
  function denyConsent() {
    consentResolved = true;
    marketingAllowed = false;
    if (denyPending) return;
    denyPending = true;
    denyToken += 1;
    var token = denyToken;
    window.setTimeout(function () {
      if (token === denyToken && denyPending && !marketingAllowed) finalizeDeny();
    }, DENY_GRACE_MS);
  }

  function applyConsent(allowed) {
    if (allowed) grantConsent();
    else denyConsent();
  }

  /* A "denied" reading from the API is never acted on: nothing is written,
     synced or injected, and nothing is wiped. Only a grant, or an explicit
     visitorConsentCollected deny, changes anything. */
  function resolveConsentFromApi() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function') return false;
    if (!privacy.marketingAllowed()) return false;
    grantConsent();
    return true;
  }

  function initializePrivacy() {
    if (marketingAllowed) return;
    if (resolveConsentFromApi()) return;

    var shopify = window.Shopify;
    if (!privacyLoadRequested && shopify && typeof shopify.loadFeatures === 'function') {
      privacyLoadRequested = true;
      shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' }
      ], function (error) {
        if (!error && !marketingAllowed) resolveConsentFromApi();
      });
    }

    if (privacyAttempts < PRIVACY_MAX_ATTEMPTS && !marketingAllowed) {
      privacyAttempts += 1;
      window.setTimeout(initializePrivacy, 250);
    }
  }

  document.addEventListener('visitorConsentCollected', function (event) {
    var detail = event && event.detail;
    if (detail && typeof detail.marketingAllowed === 'boolean') {
      applyConsent(detail.marketingAllowed);
      return;
    }
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.marketingAllowed === 'function') {
      applyConsent(privacy.marketingAllowed());
    }
  });

  /* Existing theme events are synchronization signals only. The original cart
     request and all UI behavior remain untouched. */
  document.addEventListener('cart:added', function () { syncCart(true); });
  document.addEventListener('cart:update', function (event) {
    var data = event.detail && event.detail.data;
    if (!data || data.didError === true) return;
    var source = String(data.source || '');
    if (/product|quick/i.test(source)) syncCart(true);
  });
  document.addEventListener('submit', injectCheckoutAttributes, true);

  window.ABLGoogleAttribution = {
    version: VERSION,
    sync: function () { return syncCart(true); }
  };

  initializePrivacy();
})();
